import { useCallback, useState, useMemo, useEffect, ChangeEvent } from 'react';
import { areasMonth, areasWeek, fetchResources, ELEMENTS } from './helpers';
import { format, startOfMonth, endOfMonth, getDaysInMonth } from 'date-fns';
import { darkTheme, lightTheme } from './helpers/theme';
import { useSelector, useDispatch } from 'react-redux';
import { selectPrograms, isPlannerLoading, selectChannels, selectFilteredPlans } from './store/selectors';
import { actions as plannerActions, savePlan, updatePlan } from './store/slice';

const currentMonthStart = startOfMonth(new Date());
const currentMonthEnd = endOfMonth(new Date());
const startDate = format(currentMonthStart, "yyyy-MM-dd'T'00:00:00");
const nextMonthStart = startOfMonth(new Date(currentMonthEnd.getFullYear(), currentMonthEnd.getMonth() + 1));
const endDate = format(nextMonthStart, "yyyy-MM-dd'T'24:00:00");
const scrollWidth = 10;
const plannerPadding = 10;
const channelWidth = 50;
const relativePixelWeekMode = 31.25;

export function usePlanner(plannerRef) {
  const [dayWidth, setDayWidth] = useState(1500);
  const [calendarMode, setCalendarMode] = useState('week');
  const [dates, setDates] = useState({
    start: startDate,
    end: endDate,
  });
  const dispatch = useDispatch();
  const [timelineWidth, setTimelineWidth] = useState(0);
  const programs = useSelector(selectFilteredPlans);
  const channels = useSelector(selectChannels);
  const isLoading = useSelector(isPlannerLoading);

  const channelsData = useMemo(() => channels.map(channel => ({ ...channel })), [channels]);
  const programsData = useMemo(() => programs.map(program => ({ ...program })), [programs]);

  const calculateTimelineWidth = () => {
    return plannerRef.current ? plannerRef.current.offsetWidth - scrollWidth - plannerPadding - channelWidth : 0;
  };

  useEffect(() => {
    if (plannerRef.current) {
      const timelineWidth = calculateTimelineWidth();
      setTimelineWidth(timelineWidth);
      setDayWidth((timelineWidth / getDaysInMonth(dates.start)) * relativePixelWeekMode);
    }
  }, []);

  // Handlers
  const handleChangeDate = useCallback((startDate, endDate, modeType) => {
    const modeSettings = {
      month: { mode: 'month', width: calculateTimelineWidth() * 1.05 },
      week: { mode: 'week', width: (calculateTimelineWidth() / getDaysInMonth(dates.start)) * relativePixelWeekMode },
      day: { mode: 'day', width: 1900 },
    };
    const { mode, width } = modeSettings[modeType] || modeSettings.day;
    setCalendarMode(mode);
    setDates({ start: startDate, end: endDate });
    setDayWidth(width);
  }, []);

  const handleRemoveElement = useCallback(id => {
    dispatch(plannerActions.removeElement(id));
  }, []);

  // Provide props for Timeline and layout components
  const getEpgProps = useCallback(() => ({
    channels: channelsData,
    programs: programsData,
    dayWidth,
    timelineWidth,
    calendarMode,
    dates,
  }), [channelsData, programsData, dayWidth, timelineWidth, calendarMode, dates]);

  const getLayoutProps = useCallback(() => ({
    channels: channelsData,
    programs: programsData,
    dayWidth,
    timelineWidth,
    calendarMode,
    dates,
  }), [channelsData, programsData, dayWidth, timelineWidth, calendarMode, dates]);

  return {
    isLoading,
    getEpgProps,
    getLayoutProps,
    calendarMode,
    dates,
    onChangeDate: handleChangeDate,
    removeElement: handleRemoveElement,
  };
}